/**
 * facilities.service.ts — Admin > Facilities.
 *
 * The full facility record, read and written through the same /v5/facilities
 * endpoints the corporate dashboard's Facilities page uses (FacilitiesController),
 * so a single account edits everything corp can: address, status, dates, hours,
 * social links, care team, quote / lead-form settings, the operating room and
 * anesthesia rate scales and IP addresses. The
 * request is scoped to the signed-in account by the X-Target-Company header the
 * API client adds. Writes need Admin Access.
 *
 * The lighter `listFacilities` in admin.service.ts (filters, pickers) is a
 * different, read-only shape and stays as it is.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export interface OperatingDay {
  /** "HH:MM", 24-hour. */
  open: string;
  close: string;
  closed: boolean;
}
export type OperatingHours = Record<DayOfWeek, OperatingDay>;

export type FacilityStatus = 'closed' | 'presales' | 'soft-open' | 'open';

/** facilities_addons.facilityType. */
export const FACILITY_TYPE_OPTIONS = [
  { value: 0, label: 'Standard' },
  { value: 1, label: 'Virtual' },
  { value: 2, label: 'Private' },
] as const;

export const FACILITY_STATUS_OPTIONS: { value: FacilityStatus; label: string }[] = [
  { value: 'closed', label: 'Closed' },
  { value: 'presales', label: 'Presales' },
  { value: 'soft-open', label: 'Soft Open' },
  { value: 'open', label: 'Open' },
];

/** facilities.facilityRateType. */
export const FACILITY_RATE_TYPES = [
  { value: 0, label: 'Fixed scale' },
  { value: 1, label: 'Custom' },
] as const;

/** One step of a facility's operating room or anesthesia rate scale (facility_or_fees / facility_anes_fees). */
export interface RateRow {
  hours: string;
  cost: string;
}

/** Legacy's hour steps, 0.25 to 5: the rate Increment and the generator's "per … hours". */
export const HOUR_STEP_OPTIONS: string[] = Array.from({ length: 20 }, (_, i) => String((i + 1) * 0.25));

export interface FacilityRecord {
  facilityID: number;
  name: string;
  active: boolean;
  defaultFacility: boolean;
  facilityStatus: FacilityStatus;
  facilityType: number;
  /** The street address (facilities_addons.address). */
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryID: number | null;
  phone: string;
  phoneCountryID: number | null;
  /** country_tzones.tzoneID; 0 = the account's timezone. */
  timezoneID: number;
  /** "YYYY-MM-DD", or '' when not set. */
  openDate: string;
  closeDate: string;
  softOpenStart: string;
  softOpenStop: string;
  website: string;
  facebook: string;
  twitter: string;
  instagramUrl: string;
  pinterestUrl: string;
  linkedinUrl: string;
  tiktokUrl: string;
  youtubeUrl: string;
  /** null when the facility has never had hours saved. */
  operatingHours: OperatingHours | null;
  applyHoursToCalendar: boolean;
  doctorID: number;
  coordID: number;
  nurseID: number;
  /** The address used in shortcode variables (facilities.address). */
  address: string;
  hint: string;
  taxRate: string;
  onQuotes: boolean;
  nonAdminAllowEdit: boolean;
  onNewLeadForm: boolean;
  facilityRateType: number;
  /** The rate scale's step and its last hour (facilities.hourIncrement / maxHours). */
  hourIncrement: string;
  maxHours: string;
  orRates: RateRow[];
  anRates: RateRow[];
  ipAddresses: string[];
  facilityImage: string;
}

/** What POST / PUT /facilities accept; a PUT changes only the keys it carries. */
export type FacilityPayload = Partial<{
  name: string;
  defaultFacility: boolean;
  facilityStatus: FacilityStatus;
  facilityType: number;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryID: number;
  phone: string;
  phoneCountryID: number;
  timezoneID: number;
  openDate: string;
  closeDate: string;
  softOpenStart: string;
  softOpenStop: string;
  website: string;
  facebook: string;
  facebookUrl: string;
  twitter: string;
  twitterUrl: string;
  instagramUrl: string;
  pinterestUrl: string;
  linkedinUrl: string;
  tiktokUrl: string;
  youtubeUrl: string;
  operatingHours: OperatingHours;
  applyHoursToCalendar: boolean;
  doctorID: number;
  coordID: number;
  nurseID: number;
  address: string;
  hint: string;
  taxRate: number;
  onQuotes: boolean;
  nonAdminAllowEdit: boolean;
  onNewLeadForm: boolean;
  facilityRateType: number;
  ipAddresses: string[];
  facilityImage: string;
  hourIncrement: string;
  maxHours: string;
  /** The whole rate scale, "time~fee;time~fee;"; the API replaces the facility's rows with it. */
  orArray: string;
  anArray: string;
  active: boolean;
}>;

export interface TimezoneChoice {
  value: number;
  label: string;
}

// ─── Normalizing ───────────────────────────────────────────────────────────────

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

function flag(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || v === 'true';
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function idOrNull(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** '' for the zero date and for anything that is not a plain date. */
function dateOrEmpty(v: unknown): string {
  const s = str(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !s.startsWith('0000') ? s : '';
}

const STATUSES = new Set<string>(['closed', 'presales', 'soft-open', 'open']);

/** Nine to five, every day open — corp's starting hours. */
export function defaultOperatingHours(): OperatingHours {
  return Object.fromEntries(DAYS_OF_WEEK.map((d) => [d, { open: '09:00', close: '17:00', closed: false }])) as OperatingHours;
}

function hhmm(v: unknown, fallback: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(str(v));
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : fallback;
}

/** The API's day-keyed hours (either capitalisation), filled out to all seven days. */
export function parseOperatingHours(v: unknown): OperatingHours | null {
  if (!v || typeof v !== 'object') return null;
  const obj = v as Record<string, unknown>;
  const def = defaultOperatingHours();
  return Object.fromEntries(
    DAYS_OF_WEEK.map((d) => {
      const day = obj[d] ?? obj[d.charAt(0).toUpperCase() + d.slice(1)];
      if (!day || typeof day !== 'object') return [d, def[d]];
      const o = day as Record<string, unknown>;
      return [d, { open: hhmm(o.open, def[d].open), close: hhmm(o.close, def[d].close), closed: flag(o.closed) }];
    }),
  ) as OperatingHours;
}

export function normalizeFacility(raw: Record<string, unknown>): FacilityRecord {
  const status = str(raw.facilityStatus);
  return {
    facilityID: num(raw.facilityID ?? raw.id),
    name: str(raw.name ?? raw.facilityName),
    // A missing flag reads as live, as the corp list does.
    active: raw.active === undefined || raw.active === null ? true : flag(raw.active),
    defaultFacility: flag(raw.defaultFacility),
    facilityStatus: (STATUSES.has(status) ? status : 'closed') as FacilityStatus,
    facilityType: num(raw.facilityType),
    address2: str(raw.address2),
    city: str(raw.city),
    state: str(raw.state),
    postalCode: str(raw.postalCode ?? raw.zip),
    countryID: idOrNull(raw.countryID),
    phone: str(raw.phone),
    phoneCountryID: idOrNull(raw.phoneCountryID),
    timezoneID: num(raw.timezoneID),
    openDate: dateOrEmpty(raw.openDate),
    closeDate: dateOrEmpty(raw.closeDate),
    softOpenStart: dateOrEmpty(raw.softOpenStart),
    softOpenStop: dateOrEmpty(raw.softOpenStop),
    website: str(raw.website),
    // Corp keeps Facebook and Twitter in the survey-data columns, falling back to the add-on ones.
    facebook: str(raw.facebook) || str(raw.facebookUrl),
    twitter: str(raw.twitter) || str(raw.twitterUrl),
    instagramUrl: str(raw.instagramUrl),
    pinterestUrl: str(raw.pinterestUrl),
    linkedinUrl: str(raw.linkedinUrl),
    tiktokUrl: str(raw.tiktokUrl),
    youtubeUrl: str(raw.youtubeUrl),
    operatingHours: parseOperatingHours(raw.operatingHours),
    applyHoursToCalendar: flag(raw.applyHoursToCalendar),
    doctorID: num(raw.doctorID),
    coordID: num(raw.coordID),
    nurseID: num(raw.nurseID),
    address: str(raw.address),
    hint: str(raw.hint),
    taxRate: raw.taxRate == null || raw.taxRate === '' ? '' : String(Number(raw.taxRate)),
    onQuotes: flag(raw.onQuotes),
    nonAdminAllowEdit: flag(raw.nonAdminAllowEdit),
    onNewLeadForm: flag(raw.onNewLeadForm),
    facilityRateType: num(raw.facilityRateType),
    hourIncrement: str(raw.hourIncrement).trim(),
    maxHours: str(raw.maxHours).trim(),
    orRates: parseRateArray(raw.orArray),
    anRates: parseRateArray(raw.anArray),
    ipAddresses: Array.isArray(raw.ipAddresses) ? raw.ipAddresses.map(str).filter(Boolean) : [],
    facilityImage: str(raw.facilityImage).trim(),
  };
}

// ─── Rate scales ───────────────────────────────────────────────────────────────

/** The API's "time~fee;time~fee;" string, as rows. */
export function parseRateArray(v: unknown): RateRow[] {
  return str(v)
    .split(';')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [hours = '', cost = ''] = pair.split('~');
      return { hours: hours.trim(), cost: cost.trim() };
    });
}

/** Rows back to the API's string. A row with no hours is left out: the API would save it as hour 0. */
export function serializeRateArray(rows: RateRow[]): string {
  return rows
    .filter((r) => r.hours.trim() !== '')
    .map((r) => `${r.hours.trim()}~${r.cost.trim()};`)
    .join('');
}

/**
 * Legacy's Generate Rates (admin/admin_generate_cost_table.php): one row per
 * increment up to the max hours, each priced at the started blocks of
 * `perHours` — "$500 per 1 hour" in 0.25 steps costs 500 from 0.25 to 1 hour,
 * then 1000. With no price every row costs 0, as legacy's do. Nothing without
 * an increment and a max.
 */
export function generateRateRows(increment: string, maxHours: string, price: string, perHours: string): RateRow[] {
  const inc = Number(increment);
  const max = Number(maxHours);
  const amount = Number(price);
  const per = Number(perHours);
  if (!(inc > 0) || !(max > 0)) return [];
  const priced = Number.isFinite(amount) && amount !== 0 && per > 0;
  const rows: RateRow[] = [];
  for (let i = 1; i * inc <= max + 1e-9; i++) {
    const hours = Number((i * inc).toFixed(4));
    let cost = 0;
    if (priced) {
      const running = (amount / per) * hours;
      const whole = Math.trunc(running);
      cost = whole !== 0 ? Math.ceil(whole / amount) * amount : running;
    }
    rows.push({ hours: String(hours), cost: String(Number(cost.toFixed(2))) });
  }
  return rows;
}

/** Legacy's "+": a row one increment past the last, its cost left to fill in. */
export function nextRateRow(rows: RateRow[], increment: string): RateRow {
  const inc = Number(increment) > 0 ? Number(increment) : 0.25;
  const last = rows.length > 0 ? Number(rows[rows.length - 1].hours) : 0;
  return { hours: Number.isFinite(last) ? String(Number((last + inc).toFixed(4))) : '', cost: '' };
}

// ─── Facilities ────────────────────────────────────────────────────────────────

/** Every facility in the account, deleted ones too (active false). */
export function listFacilityRecords(): Promise<FacilityRecord[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/facilities')
    .then((r) => (Array.isArray(r.data?.data) ? r.data.data : []).map(normalizeFacility));
}

export function createFacility(payload: FacilityPayload): Promise<void> {
  return apiClient.post('/facilities', payload).then(() => undefined);
}

export function updateFacility(id: number, payload: FacilityPayload): Promise<void> {
  return apiClient.put(`/facilities/${id}`, payload).then(() => undefined);
}

/** A soft delete: the facility is kept with active = 0. */
export function deleteFacility(id: number): Promise<void> {
  return apiClient.delete(`/facilities/${id}`).then(() => undefined);
}

export function restoreFacility(id: number): Promise<void> {
  return apiClient.post(`/facilities/${id}/restore`).then(() => undefined);
}

// ─── Lookups ───────────────────────────────────────────────────────────────────

/** Timezones of one country (GET /country-tzones), by title. */
export function listCountryTimezones(countryID: number): Promise<TimezoneChoice[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/country-tzones', { params: { countryID } })
    .then((r) =>
      (Array.isArray(r.data?.data) ? r.data.data : [])
        .map((t) => ({ value: num(t.tzoneID ?? t.value), label: str(t.tzTitle ?? t.label) }))
        .filter((t) => t.value > 0),
    );
}

/** The account's own timezone and country — what a new facility starts with. */
export async function getAccountFacilityDefaults(): Promise<{ timezoneID: number | null; countryID: number | null }> {
  const [details, country] = await Promise.allSettled([
    apiClient.get<ApiResponse<Record<string, unknown>>>('/account-details'),
    apiClient.get<ApiResponse<Record<string, unknown>[]>>('/accounts-country'),
  ]);
  const tz = details.status === 'fulfilled' ? idOrNull(details.value.data?.data?.timezone) : null;
  const rows = country.status === 'fulfilled' && Array.isArray(country.value.data?.data) ? country.value.data.data : [];
  const last = rows[rows.length - 1];
  return { timezoneID: tz, countryID: last ? idOrNull(last.countryID ?? last.country_id) : null };
}
